import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  CheckCircle2,
  FileSpreadsheet,
  Smartphone,
  X as XIcon,
  Minus,
} from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { BackToTop } from "@/components/BackToTop";
import { seo, SITE_URL } from "@/lib/seo";
import { PlanilhaVsAppSkeleton } from "@/components/skeletons";

const PAGE_TITLE = "Planilha de controle financeiro ou app? Comparativo 2026";
const PAGE_DESCRIPTION =
  "Planilha de controle financeiro x app: veja por que a maioria desiste da planilha em semanas, o que muda no controle financeiro pessoal e como fazer a troca.";

const COMPARISON: {
  feature: string;
  planilha: "no" | "partial" | "yes";
  app: "no" | "partial" | "yes";
  detail: string;
}[] = [
  {
    feature: "Lançar um gasto em segundos, do celular",
    planilha: "no",
    app: "yes",
    detail: "Na planilha você precisa abrir o arquivo, achar a linha e digitar fórmula certa. No app são 3 toques.",
  },
  {
    feature: "Fórmulas que quebram sozinhas",
    planilha: "no",
    app: "yes",
    detail: "Uma célula colada errado destrói o mês inteiro. No app o cálculo é automático e sempre consistente.",
  },
  {
    feature: "Gastos recorrentes automáticos",
    planilha: "partial",
    app: "yes",
    detail: "Assinaturas, aluguel e mensalidades entram sozinhos todo mês.",
  },
  {
    feature: "Metas com valor guardado",
    planilha: "partial",
    app: "yes",
    detail: "Você adiciona ou retira valores das metas e o patrimônio é atualizado na hora.",
  },
  {
    feature: "Investimentos com preço atualizado",
    planilha: "no",
    app: "yes",
    detail: "Renda fixa em % do CDI (taxa buscada automaticamente) e ações/FIIs com cotação de mercado.",
  },
  {
    feature: "Relatórios e gráficos prontos",
    planilha: "partial",
    app: "yes",
    detail: "Sem montar tabela dinâmica: categorias, evolução e comparativos já vêm prontos.",
  },
  {
    feature: "Resumo semanal por e-mail e lembretes",
    planilha: "no",
    app: "yes",
    detail: "Você é lembrado de lançar e recebe o resumo — o principal motivo de não abandonar o controle.",
  },
  {
    feature: "Análise com inteligência artificial",
    planilha: "no",
    app: "yes",
    detail: "Um assistente lê seus números e aponta onde dá para cortar.",
  },
  {
    feature: "Funciona offline / no celular",
    planilha: "partial",
    app: "yes",
    detail: "O app é instalável (PWA) e continua funcionando sem internet.",
  },
  {
    feature: "Importar e exportar dados",
    planilha: "yes",
    app: "yes",
    detail: "Dá para trazer sua planilha antiga e exportar em CSV, Excel ou PDF quando quiser.",
  },
  {
    feature: "Personalização total das fórmulas",
    planilha: "yes",
    app: "partial",
    detail: "Se você adora montar modelo próprio, a planilha ainda ganha nesse ponto.",
  },
  {
    feature: "Custo",
    planilha: "yes",
    app: "yes",
    detail: "Planilha é grátis; o Seu Bolso Tranquilo tem plano gratuito para sempre.",
  },
];

const GIVE_UP_REASONS = [
  {
    title: "Dá trabalho demais lançar",
    problem:
      "Abrir o computador, achar o arquivo e digitar cada gasto no fim do dia. Em duas semanas a preguiça vence.",
    solution:
      "Lançamento em 3 toques pelo celular, com categoria sugerida e app instalável na tela inicial.",
  },
  {
    title: "As fórmulas quebram",
    problem:
      "Uma linha colada no lugar errado e o total do mês fica maluco. Aí você perde a confiança nos números.",
    solution:
      "Nada de fórmula: os totais, saldos e percentuais são calculados automaticamente e sempre batem.",
  },
  {
    title: "Você esquece de atualizar",
    problem:
      "Sem ninguém para lembrar, o controle fica dois meses parado e não vale mais a pena recuperar.",
    solution:
      "Lembretes e resumo semanal por e-mail te trazem de volta antes de o hábito morrer.",
  },
  {
    title: "Números sem interpretação",
    problem:
      "A planilha mostra quanto você gastou, mas não diz o que fazer com isso.",
    solution:
      "Gráficos por categoria, comparativos de meses e análise com IA apontando onde cortar.",
  },
  {
    title: "Metas viram só um número parado",
    problem:
      "Uma célula com 'reserva de emergência' não te dá sensação de progresso.",
    solution:
      "Metas com barra de progresso, aportes e retiradas, somando ao seu patrimônio total.",
  },
  {
    title: "Investimentos desatualizados",
    problem:
      "Atualizar CDI, cotação de ações e FIIs na mão é inviável — a planilha fica sempre defasada.",
    solution:
      "CDI oficial atualizado automaticamente e cotações de ações e FIIs buscadas para você.",
  },
];

const STEPS = [
  "Exporte sua planilha atual em CSV (ou deixe para lá e comece do zero — leva 2 minutos).",
  "Crie sua conta gratuita e importe o arquivo em Transações › Importar.",
  "Cadastre seus gastos fixos como recorrentes para nunca mais digitá-los.",
  "Defina 1 ou 2 metas e ative o resumo semanal por e-mail.",
];

const FAQ = [
  {
    q: "Planilha de controle financeiro ainda vale a pena?",
    a: "Vale se você gosta de montar fórmulas e tem disciplina para atualizar todo dia. Para a maioria das pessoas, o custo de manutenção é justamente o motivo do abandono — um app resolve o lançamento, o cálculo e o lembrete por você.",
  },
  {
    q: "Como fazer controle financeiro pessoal sem planilha?",
    a: "Registre todos os gastos assim que acontecem, separe-os por categoria, cadastre os fixos como recorrentes e revise o resumo uma vez por semana. O Seu Bolso Tranquilo faz esses quatro passos automaticamente.",
  },
  {
    q: "Consigo importar minha planilha antiga?",
    a: "Sim. Basta exportar em CSV ou Excel e usar a importação de transações. Você também pode exportar seus dados a qualquer momento em CSV, Excel ou PDF.",
  },
  {
    q: "O app é gratuito?",
    a: "Sim, o plano gratuito é para sempre e cobre o controle financeiro pessoal completo. Recursos avançados de investimentos e IA fazem parte do Premium.",
  },
];

export const Route = createFileRoute("/planilha-vs-app")({
  head: () => {
    const base = seo({
      path: "/planilha-vs-app",
      title: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      type: "article",
      imageAlt: "Comparativo entre planilha de controle financeiro e o app Seu Bolso Tranquilo",
      breadcrumbs: [
        { name: "Início", path: "/" },
        { name: "Planilha vs App", path: "/planilha-vs-app" },
      ],
    });
    return {
      meta: base.meta,
      links: base.links,
      scripts: [
        ...base.scripts,
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: PAGE_TITLE,
            description: PAGE_DESCRIPTION,
            mainEntityOfPage: `${SITE_URL}/planilha-vs-app`,
            author: { "@type": "Organization", name: "Seu Bolso Tranquilo" },
            publisher: { "@type": "Organization", name: "Seu Bolso Tranquilo" },
          }),
        },
      ],
    };
  },
  component: PlanilhaVsApp,
  pendingComponent: PlanilhaVsAppSkeleton,
});

function Mark({ value }: { value: "no" | "partial" | "yes" }) {
  if (value === "yes")
    return <CheckCircle2 className="size-5 text-primary mx-auto" aria-label="Sim" />;
  if (value === "partial")
    return <Minus className="size-5 text-muted-foreground mx-auto" aria-label="Parcial" />;
  return <XIcon className="size-5 text-destructive mx-auto" aria-label="Não" />;
}

function PlanilhaVsApp() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/70 border-b border-border/40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <BrandLogo className="size-8" alt="Logo Seu Bolso Tranquilo" />
            <span className="font-semibold">Seu Bolso Tranquilo</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">Início</Link>
            <Link to="/aprenda" className="hover:text-foreground transition-colors">Aprenda</Link>
            <Link to="/calculadoras" className="hover:text-foreground transition-colors">Calculadoras</Link>
            <Link to="/contato" className="hover:text-foreground transition-colors">Contato</Link>
          </nav>
          <Link
            to="/auth"
            search={{ mode: "register" } as never}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Começar grátis
            <ArrowRight className="size-4" />
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12 sm:py-16">
        <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground mb-6">
          <Link to="/" className="hover:text-foreground">Início</Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">Planilha vs App</span>
        </nav>

        <div className="max-w-3xl mb-12">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary mb-4">
            <FileSpreadsheet className="size-4" />
            Comparativo honesto
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-5">
            Planilha de controle financeiro x app: qual funciona de verdade?
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            A planilha é grátis e flexível — mas 8 em cada 10 pessoas param de atualizar antes do
            segundo mês. Abaixo você vê o que cada opção resolve, por que a planilha é abandonada e
            como fazer controle financeiro pessoal sem depender de fórmulas.
          </p>
        </div>

        {/* Resumo rápido */}
        <section className="grid gap-5 sm:grid-cols-2 mb-16">
          <div className="rounded-3xl border border-border/60 bg-card p-6">
            <div className="flex items-center gap-2 mb-3">
              <FileSpreadsheet className="size-5 text-muted-foreground" />
              <h2 className="font-display text-xl font-semibold">Planilha faz sentido se…</h2>
            </div>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>Você gosta de montar e ajustar fórmulas.</li>
              <li>Precisa de um modelo muito específico do seu negócio.</li>
              <li>Tem disciplina para atualizar todos os dias, sem lembrete.</li>
            </ul>
          </div>
          <div className="rounded-3xl border border-primary/30 bg-gradient-card p-6">
            <div className="flex items-center gap-2 mb-3">
              <Smartphone className="size-5 text-primary" />
              <h2 className="font-display text-xl font-semibold">O app faz sentido se…</h2>
            </div>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>Você já tentou planilha e largou no meio do caminho.</li>
              <li>Quer lançar gastos pelo celular, na hora em que acontecem.</li>
              <li>Quer metas, investimentos e relatórios prontos, sem manutenção.</li>
            </ul>
          </div>
        </section>

        {/* Tabela */}
        <section className="mb-16">
          <h2 className="font-display text-3xl font-bold tracking-tight mb-6">
            Comparativo item a item
          </h2>
          <div className="overflow-x-auto rounded-3xl border border-border/60">
            <table className="w-full text-sm min-w-[620px]">
              <caption className="sr-only">
                Comparativo entre planilha de controle financeiro e o app Seu Bolso Tranquilo
              </caption>
              <thead>
                <tr className="bg-surface-elevated/60 text-left">
                  <th scope="col" className="px-5 py-4 font-semibold">Recurso</th>
                  <th scope="col" className="px-4 py-4 font-semibold text-center w-28">Planilha</th>
                  <th scope="col" className="px-4 py-4 font-semibold text-center w-40">Seu Bolso Tranquilo</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row) => (
                  <tr key={row.feature} className="border-t border-border/50 align-top">
                    <th scope="row" className="px-5 py-4 font-medium text-left">
                      {row.feature}
                      <span className="block text-xs font-normal text-muted-foreground mt-1">
                        {row.detail}
                      </span>
                    </th>
                    <td className="px-4 py-4 text-center"><Mark value={row.planilha} /></td>
                    <td className="px-4 py-4 text-center"><Mark value={row.app} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Por que desistem */}
        <section className="mb-16">
          <h2 className="font-display text-3xl font-bold tracking-tight mb-3">
            Por que as pessoas desistem da planilha (e o que o app resolve)
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl">
            Os motivos são sempre os mesmos. Para cada um deles existe uma solução prática — e uma
            dica para você não repetir o erro.
          </p>
          <div className="grid gap-5 sm:grid-cols-2">
            {GIVE_UP_REASONS.map((item) => (
              <article key={item.title} className="rounded-3xl border border-border/60 bg-card p-6">
                <h3 className="font-display text-lg font-semibold mb-3">{item.title}</h3>
                <p className="text-sm text-muted-foreground mb-4">{item.problem}</p>
                <p className="text-sm flex gap-2">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>{item.solution}</span>
                </p>
              </article>
            ))}
          </div>
        </section>

        {/* Como migrar */}
        <section className="mb-16">
          <h2 className="font-display text-3xl font-bold tracking-tight mb-6">
            Como sair da planilha em 4 passos
          </h2>
          <ol className="space-y-4">
            {STEPS.map((step, i) => (
              <li key={step} className="flex gap-4 rounded-2xl border border-border/60 bg-card p-5">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary font-semibold text-sm">
                  {i + 1}
                </span>
                <span className="text-sm text-muted-foreground pt-1.5">{step}</span>
              </li>
            ))}
          </ol>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="font-display text-3xl font-bold tracking-tight mb-6">
            Perguntas frequentes
          </h2>
          <div className="space-y-4">
            {FAQ.map((item) => (
              <div key={item.q} className="rounded-2xl border border-border/60 bg-card p-6">
                <h3 className="font-semibold mb-2">{item.q}</h3>
                <p className="text-sm text-muted-foreground">{item.a}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-3xl border border-primary/30 bg-gradient-card p-8 text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-3">
            Troque a planilha por 2 minutos de configuração
          </h2>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
            Controle financeiro pessoal completo, grátis para sempre. Importe sua planilha e comece
            de onde parou.
          </p>
          <Link
            to="/auth"
            search={{ mode: "register" } as never}
            className="inline-flex items-center gap-2 rounded-2xl bg-gradient-primary px-7 py-4 font-semibold text-primary-foreground shadow-glow hover:opacity-90 transition-opacity"
          >
            Criar conta grátis <ArrowRight className="size-4" />
          </Link>
          <p className="text-xs text-muted-foreground mt-6">
            Quer aprender antes? Veja os{" "}
            <Link to="/aprenda" className="text-primary hover:underline">guias de finanças pessoais</Link>{" "}
            ou use as{" "}
            <Link to="/calculadoras" className="text-primary hover:underline">calculadoras gratuitas</Link>.
          </p>
        </section>
      </main>

      <BackToTop />
    </div>
  );
}
